import ChooseAmount from "../deposit/ChooseAmount";
import { t } from "../../i18n";
import { useDispatch, useSelector } from "react-redux";
import { getDeposit } from "../../selectors/payments-selectors";
import CloseIcon from "../../utils/closeIcon";
import "./MayaMiniAppDepositPaymentComponent.scss";
import { BrandA } from "../buttons/buttons";
import useGlifeCommunication, { GLIFE_EVENTS, GLIFE_RESULTS } from "../../glife/useGlifeCommunication";
import { useEffect } from "react";
import { DepositPhase } from "../../payments/PaymentsReducer";
import { showDepositResult } from "../../payments/DepositActions";
import { Spinner } from "react-bootstrap";
import { AppMode, DepositResultCode } from "../../http/protocol";
import { AppState } from "../../RootReducer";
import ClockIcon from "../../utils/clockIcon";

interface Props {
  handleClickStartPlaying?: () => void;
}

export function GlifePaymentComponent(props: Props) {
  const dispatch = useDispatch();
  const deposit = useSelector(getDeposit);
  const appMode = useSelector((app: AppState) => app.user.appMode);
  const { handleStartDeposit, getResult } = useGlifeCommunication();
  const redirectionUrl = deposit.depositDialog?.customContext?.paymentContextResult?.paymentResponse?.actionForm?.redirectionUrl;
  const glifeResult: GLIFE_RESULTS = getResult(GLIFE_EVENTS.START_DEPOSIT);
  const externalErrorId = deposit.depositDialog?.customContext?.externalErrorId;
  const isDepositError = glifeResult === GLIFE_RESULTS.ERROR || !!deposit?.error?.length || !!externalErrorId;
  const isProviderContentPhase = deposit.phase === DepositPhase.PROVIDER_CONTENT;
  const isDepositResultPhase = deposit.phase === DepositPhase.DEPOSIT_RESULT;
  const isChooseAmountPhase = deposit.phase === DepositPhase.CHOOSE_AMOUNT;
  const shouldRenderSpinner = !(isChooseAmountPhase || isDepositResultPhase || isDepositError);
  const onResetError = () => {
    dispatch({ type: "@payments/deposit/reset/error" });
  };

  useEffect(() => {
    if (isProviderContentPhase && redirectionUrl) {
      // Check appmode native NONE, if true trigger universal link by window.location.href = redirectionUrl;
      if (appMode === AppMode.NONE) {
        window.location.href = redirectionUrl;
      }
      setTimeout(() => {
        dispatch(
          showDepositResult({
            result: DepositResultCode.PENDING_VERIFICATION,
            message: "Deposit successful",
          }) as any,
        );
      }, 300);
      handleStartDeposit(redirectionUrl);
    }
  }, [isProviderContentPhase, redirectionUrl, handleStartDeposit, dispatch, appMode]);

  useEffect(
    () => () => {
      onResetError(); // Reset error when component unmounts
    },
    [],
  );

  return (
    <div className="mayaminiapp-wrapper">
      <SpinnerBoundary shouldRenderSpinner={!deposit || shouldRenderSpinner}>
        <ErrorBoundary shouldRenderError={isDepositError} error={externalErrorId}>
          {isChooseAmountPhase && <ChooseAmount />}
          {isDepositResultPhase && (
            <div className="request-wrapper">
              <ClockIcon className={`check-icon`} animate={true} />
              <span>{t("glife.deposit.processing")}</span>
              {props.handleClickStartPlaying && (
                <BrandA className={"btn btn-cta full-width start-playing"} onClick={props.handleClickStartPlaying}>
                  {t("cashier.payment-success")}
                </BrandA>
              )}
            </div>
          )}
        </ErrorBoundary>
      </SpinnerBoundary>
    </div>
  );
}

const SpinnerBoundary = ({ shouldRenderSpinner, children }: { shouldRenderSpinner: boolean; children: React.ReactNode }) => {
  if (shouldRenderSpinner) {
    return <Spinner animation={"border"} />;
  }

  return <>{children}</>;
};

enum GlifeErrorId {
  INSUFFICIENT_FUNDS = "USER_BALANCE_NOT_ENOUGH",
  NANO_FIRST_DEPOSIT_TOO_LOW = "NANO_FIRST_DEPOSIT_TOO_LOW",
  USER_AMOUNT_EXCEED_LIMIT = "USER_AMOUNT_EXCEED_LIMIT",
}

const ErrorBoundary = ({ shouldRenderError, error, children }: { shouldRenderError: boolean; error: GlifeErrorId; children: React.ReactNode }) => {
  const getErrorMessage = (errorId: string) => {
    switch (errorId) {
      case GlifeErrorId.INSUFFICIENT_FUNDS:
        return t("glife.deposit.insufficientFunds");
      case GlifeErrorId.NANO_FIRST_DEPOSIT_TOO_LOW:
        return t("glife.deposit.firstDepositTooLow");
      case GlifeErrorId.USER_AMOUNT_EXCEED_LIMIT:
        return t("glife.deposit.userExceedLimit");
      default:
        return t("glife.deposit.error");
    }
  };
  const errorMessage = getErrorMessage(error);

  if (shouldRenderError) {
    return (
      <div className="request-wrapper">
        <CloseIcon className={`invalid-icon`} animate={true} />
        <span>{errorMessage}</span>
      </div>
    );
  }
  return <>{children}</>;
};

